import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import styles from './MicrositeLandingSection.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeVersionDrawer from '../components/MicrositeVersionDrawer/MicrositeVersionDrawer'

import MicrositeHeroSectionForm from './forms/MicrositeHeroSectionForm/MicrositeHeroSectionForm'
import MicrositeAboutSectionForm from './forms/MicrositeAboutSectionForm/MicrositeAboutSectionForm'
import MicrositeCategoriesSectionForm from './forms/MicrositeCategoriesSectionForm/MicrositeCategoriesSectionForm'
import MicrositeTestimonialsSectionForm from './forms/MicrositeTestimonialsSectionForm/MicrositeTestimonialsSectionForm'
import MicrositeContactSectionForm from './forms/MicrositeContactSectionForm/MicrositeContactSectionForm'
import MicrositeFooterSectionForm from './forms/MicrositeFooterSectionForm/MicrositeFooterSectionForm'
import MicrositeFeaturesSectionForm from './forms/MicrositeFeaturesSectionForm/MicrositeFeaturesSectionForm'
import MicrositeTeamSectionForm from './forms/MicrositeTeamSectionForm/MicrositeTeamSectionForm'
import MicrositeFaqSectionForm from './forms/MicrositeFaqSectionForm/MicrositeFaqSectionForm'
import MicrositeNotFoundSectionForm from './forms/MicrositeNotFoundSectionForm/MicrositeNotFoundSectionForm'

/**
 * SECTION_META maps the sectionKey (used in the URL and in the
 * micrositeUpdateLandingSection API contract) to its label and the
 * scaffolded form component under ./forms/. Components not yet
 * built are listed as null and render a "coming soon" placeholder
 * rather than crashing the build.
 *
 * Scaffolded form folders (from create_microsite_structure.sh),
 * for reference when wiring up the remaining 8:
 *   forms/MicrositeHeroSectionForm
 *   forms/MicrositeAboutSectionForm
 *   forms/MicrositeCategoriesSectionForm
 *   forms/MicrositeTestimonialsSectionForm
 *   forms/MicrositeContactSectionForm
 *   forms/MicrositeFooterSectionForm
 *   forms/MicrositeFeaturesSectionForm
 *   forms/MicrositeTeamSectionForm
 *   forms/MicrositeFaqSectionForm
 *   forms/MicrositeNotFoundSectionForm
 */
const SECTION_META = {
    heroSection:         { label: 'Hero banner',   Component: MicrositeHeroSectionForm },
    aboutSection:        { label: 'About us',      Component: MicrositeAboutSectionForm },
    categoriesSection:   { label: 'Categories',    Component: MicrositeCategoriesSectionForm },
    testimonialsSection: { label: 'Testimonials',  Component: MicrositeTestimonialsSectionForm },
    contactSection:      { label: 'Contact',       Component: MicrositeContactSectionForm },
    footerSection:       { label: 'Footer',        Component: MicrositeFooterSectionForm },
    featuresSection:     { label: 'Features',      Component: MicrositeFeaturesSectionForm },
    ourTeamSection:      { label: 'Our team',      Component: MicrositeTeamSectionForm },
    faqSection:          { label: 'FAQ',           Component: MicrositeFaqSectionForm },
    notFoundSection:     { label: '404 page',      Component: MicrositeNotFoundSectionForm }
}

/**
 * MicrositeLandingSection
 * Shell/router for the 10 landing page section editors. Loads the
 * section's content via micrositeGetStoreConfig (landing array),
 * finds the matching sectionKey, and renders the right sub-form
 * component, passing down content/onChange plumbing. This shell
 * owns all loading, saving, dirty-checking and version history —
 * sub-forms under ./forms/ are pure presentational components.
 */
const MicrositeLandingSection = () => {
    const { sectionKey } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const meta = SECTION_META[sectionKey] || { label: sectionKey, Component: null }

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [historyOpen, setHistoryOpen] = useState(false)

    const [content, setContent] = useState({})
    const [original, setOriginal] = useState({})
    const [isVisible, setIsVisible] = useState(true)
    const [sortOrder, setSortOrder] = useState(0)

    const isDirty = useMemo(() => JSON.stringify(content) !== JSON.stringify(original), [content, original])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadSection = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetStoreConfig')
        if (res?.status === 'success') {
            const sections = res.response?.landing || []
            const match = sections.find((s) => s.sectionKey === sectionKey)
            const nextContent = match?.content || {}
            setContent(nextContent)
            setOriginal(nextContent)
            setIsVisible(match?.isVisible ?? true)
            setSortOrder(match?.sortOrder ?? 0)
        } else {
            showToast('Could not load this section. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post, sectionKey])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadSection()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier, sectionKey])

    const handleSave = async () => {
        setSaving(true)
        const res = await post('micrositeUpdateLandingSection', {
            sectionKey,
            isVisible,
            sortOrder,
            content
        })
        setSaving(false)

        if (res?.status === 'success') {
            setOriginal(content)
            showToast(`${meta.label} saved successfully`)
        } else {
            showToast('Could not save this section. Please try again.', 'error')
        }
    }

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading {meta.label.toLowerCase()}…</span>
            </div>
        )
    }

    const SectionComponent = meta.Component

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title={meta.label}
                backTo="/microsite/landing"
                backLabel="Landing page"
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
                onHistory={() => setHistoryOpen(true)}
            />

            {SectionComponent ? (
                <SectionComponent content={content} onChange={setContent} />
            ) : (
                <div className={styles.comingSoon}>
                    <p>The editor for <strong>{meta.label}</strong> isn't built yet.</p>
                    <span>This section will be available in an upcoming update.</span>
                </div>
            )}

            <MicrositeVersionDrawer
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                tabKey={`landing_${sectionKey}`}
                storeIdentifier={activeStore?.storeIdentifier}
                vendorIdentifier={activeStore?.vendorIdentifier}
                onRolledBack={loadSection}
            />
        </div>
    )
}

export default MicrositeLandingSection